import { redirect, type DataFunctionArgs } from '@remix-run/node'
import { type MetaFunction, useLoaderData } from '@remix-run/react'
import { Avatar, AvatarImage } from '#app/components/ui/avatar.tsx'
import { SPONSORS } from '#app/constants/sponsors.constants.ts'

export async function loader({ params }: DataFunctionArgs) {
	return (
		SPONSORS.find(sponsor => sponsor.id == params.sponsor_id) ??
		redirect('/sponsor')
	)
}
const SponsorName = () => {
	const {
		companyName,
		subHeader,
		imgSrc,
		description,
		website,
		phone,
		email,
		features,
	} = useLoaderData<typeof loader>()

	return (
		<div className="flex items-center justify-center pt-2">
			<div className="group relative mx-auto mb-6 mt-1 w-full min-w-0 max-w-sm break-words rounded-xl border bg-muted shadow-2xl dark:border-gray-700 dark:bg-gray-800 md:max-w-2xl">
				<div className="pb-6">
					<div className="flex flex-wrap justify-center">
						<Avatar className="m-2 h-24 w-24">
							<AvatarImage
								src={imgSrc}
								alt="sponsor image"
								className="object-contain"
							/>
						</Avatar>
					</div>
					<div className="text-center">
						<h3 className="mb-1 text-2xl font-bold leading-normal text-gray-700 dark:text-gray-300">
							{companyName}
						</h3>
						<div className="mx-auto flex w-full flex-row justify-center space-x-2 text-center">
							<div className="font-mono text-xl font-bold italic tracking-wide text-gray-600 dark:text-gray-300">
								{subHeader}
							</div>
						</div>
					</div>
					<div className="flex justify-center gap-6 pt-2">
						<a
							// eslint-disable-next-line remix-react-routes/use-link-for-routes
							href={`tel:${phone}`}
							target="_blank"
							rel="noreferrer"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="1.5"
								stroke="currentColor"
								className="h-6 w-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
								/>
							</svg>
						</a>
						{email && (
							// eslint-disable-next-line remix-react-routes/use-link-for-routes
							<a href={`mailto:${email}`} target="_blank" rel="noreferrer">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									stroke="currentColor"
									className="h-6 w-6"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M16.5 12a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 1 0-2.636 6.364M16.5 12V8.25"
									/>
								</svg>
							</a>
						)}
						<a href={website} target="_blank" rel="noreferrer">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="1.5"
								stroke="currentColor"
								className="h-6 w-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
								/>
							</svg>
						</a>
					</div>
					<div className="mx-6 mt-0 pt-6 text-center dark:border-gray-700/50">
						<div className="flex flex-wrap justify-center">
							<div className="w-full px-6">
								{description?.split('\n').map((paragraph, index) => (
									<p
										key={index}
										className="mb-4 font-light leading-relaxed text-gray-600 dark:text-gray-400"
									>
										{paragraph}
									</p>
								))}
							</div>
							{features && (
								<ul className="list-disc space-y-3 pl-5 text-left font-light leading-relaxed text-gray-600 marker:text-iagtGold dark:text-gray-400">
									{features.map((feature, index) => (
										<li key={index}>{feature}</li>
									))}
								</ul>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.companyName
	return [
		{ title: `${displayName} | IAGT` },
		{
			name: 'description',
			content: `Sponsor ${displayName} on IAGT`,
		},
	]
}

export default SponsorName
